/**
 * API client for saved views (via pro-api → user-service).
 */

import { authenticatedProApiFetch } from './pro-api.client';

export type SavedViewVisibility = 'self' | 'org';

export type SavedViewConfig = {
  columnFilters?: Array<{ id: string; value: unknown }>;
  search?: string;
  sort?: { sort_by: string; order: 'asc' | 'desc' } | null;
  visibleFilterIds?: string[];
};

export type SavedView = {
  id: string;
  ownerUserId: string;
  ownerName?: string | null;
  resourceType: string;
  listId: string;
  name: string;
  icon: string;
  color: string;
  visibility: SavedViewVisibility;
  config: SavedViewConfig;
  createdAt: string;
  updatedAt: string;
};

export type SavedViewsListResponse = {
  personal: SavedView[];
  sharedAvailable: SavedView[];
};

export type CreateSavedViewInput = {
  resourceType: string;
  listId: string;
  name: string;
  icon: string;
  color: string;
  visibility: SavedViewVisibility;
  config: SavedViewConfig;
};

export type UpdateSavedViewInput = Partial<{
  name: string;
  icon: string;
  color: string;
  visibility: SavedViewVisibility;
  config: SavedViewConfig;
  listId: string;
}>;

export function savedViewsQueryKey(resourceType: string) {
  return ['saved-views', resourceType] as const;
}

export async function listSavedViews(
  token: string,
  resourceType: string,
): Promise<SavedViewsListResponse> {
  const qs = new URLSearchParams({ resourceType });
  return authenticatedProApiFetch<SavedViewsListResponse>(
    `/saved-views?${qs.toString()}`,
    token,
    { method: 'GET' },
  );
}

export async function createSavedView(
  token: string,
  input: CreateSavedViewInput,
): Promise<SavedView> {
  return authenticatedProApiFetch<SavedView>('/saved-views', token, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateSavedView(
  token: string,
  id: string,
  input: UpdateSavedViewInput,
): Promise<SavedView> {
  return authenticatedProApiFetch<SavedView>(`/saved-views/${id}`, token, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export async function deleteSavedView(
  token: string,
  id: string,
): Promise<{ ok: boolean }> {
  return authenticatedProApiFetch<{ ok: boolean }>(`/saved-views/${id}`, token, {
    method: 'DELETE',
  });
}
